/**
 * App — root component with routing.
 *
 * React Router 7 data routes with auth guards.
 * Unauthenticated users see the login page; authenticated users see the layout.
 */
import { lazy, Suspense, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api, openApiContent } from './lib/api.js';
import { useAuthStore, type CurrentUser } from './store/auth.js';
import { Layout } from './shell/Layout.js';
import { toast, ToastContainer } from './shell/Toast.js';

// Pages — lazy import not needed for a PWA shell
import { DashboardPage } from './features/dashboard/pages/DashboardPage.js';
import { EmployeeListPage } from './features/employees/pages/EmployeeListPage.js';
import { EmployeeDetailPage } from './features/employees/pages/EmployeeDetailPage.js';
import { EmployeeDirectoryPage } from './features/employees/pages/EmployeeDirectoryPage.js';
import { OrgChartPage } from './features/employees/pages/OrgChartPage.js';
import { ReviewListPage } from './features/reviews/pages/ReviewListPage.js';
import { ReviewDetailPage } from './features/reviews/pages/ReviewDetailPage.js';
import { RecognitionPage } from './features/recognition/pages/RecognitionPage.js';
import { DisciplinaryPage } from './features/disciplinary/pages/DisciplinaryPage.js';
import { ConversationsPage } from './features/conversations/pages/ConversationsPage.js';
import { TimeOffPage } from './features/timeoff/pages/TimeOffPage.js';
import { OnboardingPage } from './features/onboarding/pages/OnboardingPage.js';
import { OffboardingPage } from './features/offboarding/pages/OffboardingPage.js';
import { DocumentsPage } from './features/documents/pages/DocumentsPage.js';
import { AuditPage } from './features/audit/pages/AuditPage.js';
import { SettingsPage } from './features/me/pages/SettingsPage.js';
import { VendorsPage } from './features/vendors/pages/VendorsPage.js';
import { NaidPage } from './features/naid/pages/NaidPage.js';
import { LoginPage } from './pages/LoginPage.js';
import { CHAT_ENABLED } from '@shared/features';
import { ChatClientProvider } from './features/chat/ChatClientProvider.js';

/** Stream Chat SDK is large — load only when visiting /chat. */
const ChatPage = CHAT_ENABLED
  ? lazy(() =>
    import('./features/chat/pages/ChatPage.js').then((m) => ({ default: m.ChatPage })),
  )
  : null;

function ChatRoute() {
  if (!ChatPage) return <Navigate to="/" replace />;
  return (
    <Suspense
      fallback={
        <div className="flex min-h-[40vh] items-center justify-center text-sm font-bold uppercase tracking-wide text-[var(--color-muted)]">
          Loading chat…
        </div>
      }
    >
      <ChatPage />
    </Suspense>
  );
}

function AuthenticatedShell({ children }: { children: React.ReactNode }) {
  if (!CHAT_ENABLED) {
    return <Layout>{children}</Layout>;
  }
  return (
    <ChatClientProvider>
      <Layout>{children}</Layout>
    </ChatClientProvider>
  );
}

// Fetch the current user — single source of role truth
function useCurrentUser() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<CurrentUser>('/api/me'),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

function AuthGate() {
  const { data: user, isLoading, isError } = useCurrentUser();
  const setUser = useAuthStore((s) => s.setUser);

  useEffect(() => {
    setUser(user ?? null);
  }, [user, setUser]);

  useEffect(() => {
    const openProtectedLink = (event: MouseEvent) => {
      const link = (event.target as Element | null)?.closest('a[target="_blank"]') as HTMLAnchorElement | null;
      if (!link || !link.href.startsWith(`${window.location.origin}/api/`)) return;

      event.preventDefault();
      void openApiContent(link.pathname + link.search).catch((error: unknown) => {
        toast('error', error instanceof Error ? error.message : 'Failed to open file.');
      });
    };

    document.addEventListener('click', openProtectedLink, true);
    return () => document.removeEventListener('click', openProtectedLink, true);
  }, []);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--color-paper)]">
        <div className="flex flex-col items-center gap-3">
          <svg className="animate-spin h-8 w-8 text-[var(--color-accent)]" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-sm font-bold uppercase tracking-wide text-[var(--color-muted)]">Loading…</p>
        </div>
      </div>
    );
  }

  if (isError || !user) {
    return <LoginPage />;
  }

  return (
    <AuthenticatedShell>
      <Outlet />
    </AuthenticatedShell>
  );
}

// Role guard — wraps routes that require specific roles
function RoleGuard({ roles, children }: { roles: ('Admin' | 'Manager' | 'Employee')[]; children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  if (!user || !roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <AuthGate />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'employees', element: <EmployeeListPage /> },
      { path: 'employees/:id', element: <EmployeeDetailPage /> },
      { path: 'directory', element: <EmployeeDirectoryPage /> },
      { path: 'vendors', element: <VendorsPage /> },
      { path: 'org-chart', element: <OrgChartPage /> },
      { path: 'reviews', element: <ReviewListPage /> },
      { path: 'reviews/:id', element: <ReviewDetailPage /> },
      { path: 'recognition', element: <RecognitionPage /> },
      {
        path: 'disciplinary',
        element: <RoleGuard roles={['Admin', 'Manager', 'Employee']}><DisciplinaryPage /></RoleGuard>,
      },
      {
        path: 'conversations',
        element: <RoleGuard roles={['Admin', 'Manager', 'Employee']}><ConversationsPage /></RoleGuard>,
      },
      { path: 'time-off', element: <TimeOffPage /> },
      { path: 'chat', element: <ChatRoute /> },
      {
        path: 'onboarding',
        element: (
          <RoleGuard roles={['Admin', 'Manager']}>
            <OnboardingPage />
          </RoleGuard>
        ),
      },
      {
        path: 'offboarding',
        element: (
          <RoleGuard roles={['Admin', 'Manager']}>
            <OffboardingPage />
          </RoleGuard>
        ),
      },
      {
        path: 'naid',
        element: (
          <RoleGuard roles={['Admin', 'Manager']}>
            <NaidPage />
          </RoleGuard>
        ),
      },
      {
        path: 'documents',
        element: (
          <RoleGuard roles={['Admin', 'Manager']}>
            <DocumentsPage />
          </RoleGuard>
        ),
      },
      {
        path: 'audit',
        element: <RoleGuard roles={['Admin']}><AuditPage /></RoleGuard>,
      },
      { path: 'users', element: <Navigate to="/employees" replace /> },
      { path: 'settings', element: <SettingsPage /> },
    ],
  },
]);

export default function App() {
  return (
    <>
      <RouterProvider router={router} />
      <ToastContainer />
    </>
  );
}
